import { observer } from 'mobx-react-lite';
import { bodyPlaceholderText } from 'owa-locstrings/lib/strings/bodyplaceholdertext.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import AttachmentWellHost from 'owa-calendar-meeting-forms-common-store/lib/store/schema/AttachmentWellHost';
import FullComposeEditorAreaBottom from './FullComposeEditorAreaBottom';
import getFullComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getFullComposeViewState';
import isInPopOutWindow from 'owa-calendar-compose-form-store/lib/selectors/isInPopOutWindow';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import { ReactEditor, focusToEditor } from 'owa-editor';
import toggleRibbon, { resizeRibbon } from '../utils/toggleRibbon';
import updateLastFocusedControl from 'owa-addins-editor-plugin/lib/actions/updateLastFocusedControl';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { AttachmentWellView } from 'owa-attachment-well-view';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { LastFocusedControl } from 'owa-editor-addin-plugin-types/lib/utils/AddinViewState';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import Droppable from 'owa-dnd/lib/components/Droppable';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { getPluginArray } from 'owa-editor-plugins-list';
import { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { LazyComposeDropHint } from 'owa-attachment-drag-and-drop';
import { LocalFilesDragData } from 'owa-dnd/lib/utils/LocalFilesDragData';
import {
    CreateAttachmentsProperties,
    lazyConvertImageViewAttachmentToInlineAttachment,
} from 'owa-attachment-well-data';
import { lazyInsertInlineImageFromAttachmentWell } from 'owa-editor-inlineimage-plugin';
import { createAttachmentsFromFiles } from '../actions/createAttachmentsFromFiles';
import getEventClientItemId from 'owa-calendar-compose-form-store/lib/utils/getEventClientItemId';
import { logUsage } from 'owa-analytics';
import { createLocalComputerFiles } from 'owa-attachment-file-types';
import {
    onIsDraftChanged,
    saveEventDraft,
    onAttachmentDeleted,
} from 'owa-calendar-compose-form-actions';
import generateBorderStyle from 'owa-calendar-forms-common/lib/utils/generateBorderStyle';
import { isConnectedAccount } from 'owa-accounts-store';
import { setTypeOfItemBeingDragged } from 'owa-calendar-compose-form-store/lib/mutators/fullcomposeviewstate/dragAndDropMutators';
import setDockingTriggerPart from 'owa-editor-docking-plugin/lib/actions/setDockingTriggerPart';
import createFullComposeEditorPlugins from '../utils/createFullComposeEditorPlugins';
import { logTelemetryForJoinMeetingLink } from 'owa-online-meeting/lib/utils/logTelemetryForJoinMeetingLink';
import { ViewType } from 'owa-calendar-actions';
import getBottomtDockingHeight from '../utils/getBottomDockingHeight';
import { showDockedBottom } from 'owa-editor-docking-plugin';
import { Link } from '@fluentui/react/lib/Link';
import { isFeatureEnabled } from 'owa-feature-flags';
import { editorRowPlaceholderText } from '../strings.locstring.json';
import { showExpandedEditor } from 'owa-calendar-compose-form-store/lib/mutators/composeViewStateMutators';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';

let styles = require('./FullComposeEditorArea.scss');
const classNames = require('classnames/bind').bind(styles);
const SCROLL_CONTAINER_SELECTOR = '.customScrollBar';

export interface FullComposeEditorAreaProps {
    calendarColorScheme: ColorScheme;
}

export default observer(function FullComposeEditorArea(props: FullComposeEditorAreaProps) {
    const editorContainerRef = React.useRef<HTMLDivElement>();
    const callJoinMeetingsTelemetryLogger = (onClickEvent: any) => {
        logTelemetryForJoinMeetingLink(
            'EditForm' as ViewType,
            onClickEvent,
            event,
            true /** isCTRLKeyAndClickEnforced */
        );
    };

    React.useEffect(() => {
        if (isInPopOutWindow()) {
            window.addEventListener('resize', resizeRibbon_0);
        }

        editorContainerRef.current?.addEventListener('click', callJoinMeetingsTelemetryLogger);

        return () => {
            editorContainerRef.current?.removeEventListener(
                'click',
                callJoinMeetingsTelemetryLogger
            );

            window.removeEventListener('resize', resizeRibbon_0);
        };
    }, []);

    const editorEverFocused = React.useRef(false);
    const fullComposeViewState = getFullComposeViewState();
    const trySetFocus = () => {
        focusToEditor(fullComposeViewState?.bodyViewState);
    };
    const onFocusToEditor = (focusEvent?: React.FocusEvent<HTMLDivElement>) => {
        updateLastFocusedControl(fullComposeViewState.addin, LastFocusedControl.Editor);
        if (!editorEverFocused.current && !fullComposeViewState.isRibbonShown) {
            toggleRibbon();
            editorEverFocused.current = true;
        }
    };
    const pluginList = React.useMemo(
        () => getPluginArray(createFullComposeEditorPlugins(fullComposeViewState)),
        [fullComposeViewState]
    );
    const expandEditor = () => {
        showExpandedEditor(true);
        onFocusToEditor();
    };

    const renderEditor = (): JSX.Element => {
        return (
            <div className={styles.editor} ref={editorWrapperDivCallback}>
                <ReactEditor
                    viewState={fullComposeViewState.bodyViewState}
                    classNames={styles.editorContent}
                    plugins={pluginList}
                    watermark={loc(bodyPlaceholderText)}
                    onFocus={onFocusToEditor}
                    scrollContainerSelector={SCROLL_CONTAINER_SELECTOR}
                    isDarkMode={fullComposeViewState.isDarkMode}
                />
            </div>
        );
    };

    const onAttachmentMovedInline = async (
        attachment: AttachmentFullViewState,
        contentId: string
    ) => {
        const { attachmentWell } = getComposeStore().uiState as AttachmentWellHost;
        const convertImageViewAttachmentToInlineAttachment = await lazyConvertImageViewAttachmentToInlineAttachment.import();
        convertImageViewAttachmentToInlineAttachment(attachmentWell, attachment, contentId);

        if (isFeatureEnabled('cmp-inlineImageV2')) {
            const attachmentModel = getAttachment(attachment.attachmentId);
            if (attachmentModel) {
                const insertImageFromAttachmentWell = await lazyInsertInlineImageFromAttachmentWell.import();
                insertImageFromAttachmentWell(
                    attachmentModel.model.AttachmentOriginalUrl,
                    fullComposeViewState.bodyViewState,
                    fullComposeViewState.inlineImageV2
                );
            }
        }
    };

    const renderAttachmentWell = (): JSX.Element => {
        let { event, uiState } = getComposeStore();
        const eventItemId: ClientItemId = {
            ...event.ItemId,
            mailboxInfo: event.ParentFolderId.mailboxInfo,
        };
        const attachmentWellHost = uiState as AttachmentWellHost;
        return (
            <div className={styles.attachmentwell}>
                <AttachmentWellView
                    attachmentWellViewState={attachmentWellHost.attachmentWell}
                    parentItemId={eventItemId}
                    isDraft={event.IsDraft}
                    onAttachmentDeletedCallback={onAttachmentDeletedCallback}
                    moveAttachmentToInlineHandler={onAttachmentMovedInline}
                    onLostFocus={trySetFocus}
                />
            </div>
        );
    };

    const renderFullComposeEditorContainer = (): JSX.Element => {
        const { uiState } = getComposeStore();
        const attachmentWellHost = uiState as AttachmentWellHost;
        const hasAttachment =
            (attachmentWellHost.attachmentWell.docViewAttachments &&
                attachmentWellHost.attachmentWell.docViewAttachments.length > 0) ||
            (attachmentWellHost.attachmentWell.imageViewAttachments &&
                attachmentWellHost.attachmentWell.imageViewAttachments.length > 0);
        const editorContainerClassNames = classNames(styles.editorContainer, {
            hasAttachment: hasAttachment,
        });
        return (
            <div ref={editorContainerRef} className={editorContainerClassNames}>
                {attachmentWellHost.attachmentWell && renderAttachmentWell()}
                {renderEditor()}
                <FullComposeEditorAreaBottom
                    className={
                        showDockedBottom(fullComposeViewState.docking)
                            ? styles.hidden
                            : borderColorStyle.dockingBorder
                    }
                />
            </div>
        );
    };

    const renderEditorArea = (): JSX.Element => {
        return (
            <div className={classNames(styles.editorArea, borderColorStyle.fullBorder)}>
                {
                    /* Block attachments when creating event in connected account */
                    !isConnectedAccount(event.ParentFolderId.mailboxInfo.userIdentity) ? (
                        <Droppable
                            dropViewState={fullComposeViewState.dropViewState}
                            onDrop={onDrop}
                            canDrop={canDrop}
                            shouldUseCopyAsDropEffect={true}
                            shouldIgnoreTransientOnDragLeave={true}>
                            {renderFullComposeEditorContainer()}
                            {renderDropHint()}
                        </Droppable>
                    ) : (
                        renderFullComposeEditorContainer()
                    )
                }
            </div>
        );
    };

    const renderDehydratedRow = (): JSX.Element => {
        return (
            <div
                className={classNames(
                    styles.descriptionRowContainer,
                    borderColorStyle.bottomBorder
                )}>
                <Link className={styles.editorLink} onClick={expandEditor}>
                    {loc(editorRowPlaceholderText)}
                </Link>
            </div>
        );
    };

    const { event, uiState } = getComposeStore();
    const borderColorStyle = generateBorderStyle(props.calendarColorScheme);
    if (!event) {
        return null;
    }
    const showDehydratedDescription =
        isFeatureEnabled('cal-mf-dehydrateDescription') && !uiState.showExpandedEditor;
    return showDehydratedDescription ? renderDehydratedRow() : renderEditorArea();
});

function editorWrapperDivCallback(ref: HTMLDivElement) {
    const fullComposeViewState = getFullComposeViewState();
    if (fullComposeViewState?.docking) {
        setDockingTriggerPart(fullComposeViewState.docking, ref);
    }
}

function resizeRibbon_0() {
    window.requestAnimationFrame(resizeRibbon);
}

function onAttachmentDeletedCallback(
    parentItemId: ClientItemId,
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType
) {
    onAttachmentDeleted(attachmentId, attachment);
}

function classicAttachmentsEnabled() {
    return getAttachmentPolicy().ClassicAttachmentsEnabled;
}

function saveEventAsDraftIfNewEvent(): void {
    // Save the event as a draft if new to ensure there is
    // an event which attachments can be added to
    if (!getEventClientItemId()) {
        onIsDraftChanged(true);
        saveEventDraft();
    }
}

function renderDropHint(): JSX.Element {
    const fullComposeViewState = getFullComposeViewState();
    const shouldRenderDropHint: boolean = fullComposeViewState && shouldShowComposeDropHint();
    return shouldRenderDropHint ? (
        <LazyComposeDropHint
            editorViewState={fullComposeViewState.bodyViewState}
            dockingViewState={fullComposeViewState.docking}
            dropViewState={fullComposeViewState.dropViewState}
            dataTransferType={fullComposeViewState.typeOfItemBeingDragged}
            getBottomDockingHeight={getBottomtDockingHeight}
        />
    ) : null;
}

function shouldShowComposeDropHint(): boolean {
    // If classic attachments are disabled then we disable drag and drop
    if (!classicAttachmentsEnabled()) {
        return false;
    }
    const fullComposeViewState = getFullComposeViewState();

    return (
        fullComposeViewState.showDropHint &&
        fullComposeViewState.typeOfItemBeingDragged === DraggableItemTypes.LocalFile
    );
}

function setDataItemType(newValue: string) {
    const fullComposeViewState = getFullComposeViewState();

    if (fullComposeViewState.typeOfItemBeingDragged != newValue) {
        setTypeOfItemBeingDragged(newValue);
    }
}

function canDrop(dragInfo: DragData) {
    // If classic attachments are disabled then we disable drag and drop
    if (!classicAttachmentsEnabled()) {
        return false;
    }
    const itemType = dragInfo.itemType;
    return itemType === DraggableItemTypes.LocalFile;
}

function onDrop(dragInfo: DragData) {
    setDataItemType(null);
    saveEventAsDraftIfNewEvent();
    let attachmentFileList = null;
    if (dragInfo.itemType === DraggableItemTypes.LocalFile) {
        const filesDragData = dragInfo as LocalFilesDragData;
        attachmentFileList = createLocalComputerFiles(filesDragData.files);
    }
    if (attachmentFileList) {
        logUsage('AttachmentDragAndDropCalendar', [dragInfo.itemType]);
        const baseProperties = { isInline: false, isHiddenInline: false };
        const properties: CreateAttachmentsProperties = {
            ...baseProperties,
            shouldShare: false,
        };
        createAttachmentsFromFiles(attachmentFileList, properties);
    }
}
